import type { SourceId } from '@sourcr/shared';
import { SOURCE_LABELS } from '@sourcr/shared';

interface SourceBadgeProps {
  sourceId: SourceId;
  size?: 'sm' | 'md';
  showLabel?: boolean;
}

const SOURCE_COLORS: Record<SourceId, { bg: string; color: string }> = {
  linkedin: { bg: '#0A66C2', color: '#fff' },
  github: { bg: '#24292f', color: '#fff' },
  pool: { bg: '#16A34A', color: '#fff' },
  djinni: { bg: '#00B4A0', color: '#fff' },
  dou: { bg: '#FF6B35', color: '#fff' },
  workua: { bg: '#1677FF', color: '#fff' },
  robotaua: { bg: '#E31E24', color: '#fff' },
  telegram: { bg: '#2AABEE', color: '#fff' },
  instagram: { bg: '#E4405F', color: '#fff' },
};

/** Single-letter abbreviation glyph for each source */
const SOURCE_GLYPHS: Record<SourceId, string> = {
  linkedin: 'in',
  github: 'gh',
  pool: '★',
  djinni: 'dj',
  dou: 'D',
  workua: 'W',
  robotaua: 'R',
  telegram: 'tg',
  instagram: 'ig',
};

export function SourceBadge({ sourceId, size = 'md', showLabel = false }: SourceBadgeProps) {
  const colors = SOURCE_COLORS[sourceId];
  const glyph = SOURCE_GLYPHS[sourceId];
  const dim = size === 'sm' ? 20 : 26;
  const fontSize = size === 'sm' ? 9 : 11;

  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
      title={SOURCE_LABELS[sourceId]}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: dim,
          height: dim,
          borderRadius: 6,
          backgroundColor: colors.bg,
          color: colors.color,
          fontFamily: 'var(--font-mono)',
          fontSize,
          fontWeight: 700,
          letterSpacing: '-0.5px',
          flexShrink: 0,
        }}
      >
        {glyph}
      </span>
      {showLabel && (
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {SOURCE_LABELS[sourceId]}
        </span>
      )}
    </span>
  );
}

/** Stacked row of source badges (for candidate list) */
export function SourceBadgeRow({ sourceId }: { sourceId: SourceId }) {
  return <SourceBadge sourceId={sourceId} size="sm" />;
}
